import { useMemo } from 'react';

import { useQuery, useQueryClient } from '@tanstack/react-query';

import { getOfferings } from '@arrived/api_v2';
import { FEATURE_FLAGS, useFeature } from '@arrived/feature-flags';
import { Offering, OfferingQueryParams, OFFERINGS_SORT_PROPERTY_RANK_DESC } from '@arrived/models';

import { UseQueryOptions, UseQueryResult } from '../queries.utils';
import { offeringKeyFn, offeringsKeyFn } from './offerings.keys';

function useDefaultOfferingQueryParams(query?: OfferingQueryParams): OfferingQueryParams {
  const { on, value } = useFeature<number>(FEATURE_FLAGS.OFFERING_REQUEST_SIZE);

  return useMemo(() => ({ ...query, size: query?.size ?? (on && value ? value : undefined) }), [query, on, value]);
}

export function useOfferingsQuery(
  query?: OfferingQueryParams,
  options?: UseQueryOptions<Offering[]>
): UseQueryResult<Offering[]> {
  const newQuery = useDefaultOfferingQueryParams({ size: 1000, sort: OFFERINGS_SORT_PROPERTY_RANK_DESC, ...query });

  const queryClient = useQueryClient();

  return useQuery(offeringsKeyFn(newQuery), () => getOfferings(newQuery), {
    ...options,
    onSettled: (data, error) => {
      data?.forEach((offering) => {
        queryClient.setQueryData(offeringKeyFn(offering.id), offering, { updatedAt: Date.now() });
        queryClient.setQueryData(offeringKeyFn(offering.shortName), offering, { updatedAt: Date.now() });
      });

      options?.onSettled?.(data, error);
    },
  });
}
